import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, TextField, IconButton,
  LinearProgress,
  MenuItem,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import axios from 'axios';
import api from '../../../api/axiosInterceptor';
import { API_BASE_URL } from '../../../config';
import TableComponent from '../../../Components/TableComponent/TableComponent';
import { LoadingSpinner } from '../../../Components/spinner/spinner';
import { SnackBarComponent } from '../../../Components/snackBar/snackBar';

// Fetch FE Base list for dropdown
const useFetchFeBaseList = () => {
  const [list, setList] = useState([]);
  useEffect(() => {
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-base/`)
      .then(res => setList(res.data))
      .catch(() => setList([]));
  }, []);
  return list;
};

export const AdminFePersonalPropertiesMasterComponent = () => {
  const feBaseList = useFetchFeBaseList();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form fields with value, error, helperText
  const [formFields, setFormFields] = useState({
    financial_establishment: { value: '', error: false, helperText: '' },
    property_type: { value: '', error: false, helperText: '' },
    location: { value: '', error: false, helperText: '' },
    date_of_aquisition: { value: '', error: false, helperText: '' },
    value: { value: '', error: false, helperText: '' },
    type_of_aquisition: { value: '', error: false, helperText: '' },
    doc: { value: null, error: false, helperText: '' },
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');

  const columns = [
    { id: 'slNo', field: 'slNo', headerName: 'Sl No.', width: 70 },
    { id: 'financial_establishment', field: 'financial_establishment', headerName: 'FE Base', width: 200 },
    { id: 'property_type', field: 'property_type', headerName: 'Property Type', width: 150 },
    { id: 'location', field: 'location', headerName: 'Location', width: 200 },
    { id: 'date_of_aquisition', field: 'date_of_aquisition', headerName: 'Date of Acquisition', width: 150 },
    { id: 'value', field: 'value', headerName: 'Value', width: 150 },
    { id: 'type_of_aquisition', field: 'type_of_aquisition', headerName: 'Type of Acquisition', width: 180 },
    {
      id: 'doc',
      field: 'doc',
      headerName: 'Document',
      width: 120,
      renderCell: params =>
        params.value ? <a href={params.value} target="_blank" rel="noopener noreferrer">View</a> : 'No Doc',
    },
    {
      id: 'actions',
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      renderCell: params => (
        <>
          <IconButton color="primary" onClick={() => handleEdit(params.row)} disabled={uploading}>
            <Edit />
          </IconButton>
          <IconButton color="error" onClick={() => handleDeleteConfirmation(params.row)} disabled={uploading}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  const fetchPersonalProperties = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/fe-mgmt/fe-personal-properties/`)
      .then(response => {
        const apiData = response.data.map((item, index) => {
          const feBaseObj = feBaseList.find(fe => fe.id === (item.financial_establishment?.id || item.financial_establishment));
          return {
            id: item.id,
            slNo: index + 1,
            financial_establishment: feBaseObj ? feBaseObj.name_of_fe : (item.financial_establishment?.name_of_fe || item.financial_establishment),
            property_type: item.property_type,
            location: item.location,
            date_of_aquisition: item.date_of_aquisition,
            value: item.value,
            type_of_aquisition: item.type_of_aquisition,
            doc: item.doc,
          };
        });
        setRows(apiData);
        setLoading(false);
      })
      .catch(() => {
        setSnackbarMessage('Failed to load Personal Properties.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (feBaseList.length > 0) fetchPersonalProperties();
  }, [feBaseList]);

  const handleSnackbarClose = () => setSnackbarOpen(false);

  const resetFormFields = () => {
    setFormFields({
      financial_establishment: { value: '', error: false, helperText: '' },
      property_type: { value: '', error: false, helperText: '' },
      location: { value: '', error: false, helperText: '' },
      date_of_aquisition: { value: '', error: false, helperText: '' },
      value: { value: '', error: false, helperText: '' },
      type_of_aquisition: { value: '', error: false, helperText: '' },
      doc: { value: null, error: false, helperText: '' },
    });
    setUploadProgress(0);
  };

  const handleAddItem = () => {
    setIsEditMode(false);
    resetFormFields();
    setOpenFormDialog(true);
  };

  const handleEdit = (item) => {
    setIsEditMode(true);
    setSelectedItem(item);
    const feBaseObj = feBaseList.find(fe => fe.name_of_fe === item.financial_establishment || fe.id === item.financial_establishment);

    setFormFields({
      financial_establishment: { value: feBaseObj ? feBaseObj.id : '', error: false, helperText: '' },
      property_type: { value: item.property_type, error: false, helperText: '' },
      location: { value: item.location, error: false, helperText: '' },
      date_of_aquisition: { value: item.date_of_aquisition, error: false, helperText: '' },
      value: { value: item.value, error: false, helperText: '' },
      type_of_aquisition: { value: item.type_of_aquisition, error: false, helperText: '' },
      doc: { value: null, error: false, helperText: '' },
    });
    setUploadProgress(0);
    setOpenFormDialog(true);
  };

  const handleDeleteConfirmation = (item) => {
    setDeleteItem(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    api.delete(`/fe-mgmt/fe-personal-properties/${deleteItem.id}/`)
      .then(() => {
        setSnackbarMessage('Personal Property deleted successfully.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        fetchPersonalProperties();
      })
      .catch(() => {
        setSnackbarMessage('Failed to delete Personal Property.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      })
      .finally(() => {
        setDeleteDialogOpen(false);
        setDeleteItem(null);
      });
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'financial_establishment':
        if (!value) return 'FE Base is required';
        break;
      case 'property_type':
        if (!value.trim()) return 'Property type is required';
        break;
      case 'location':
        if (!value.trim()) return 'Location is required';
        break;
      case 'date_of_aquisition':
        if (!value) return 'Date of acquisition is required';
        break;
      case 'value':
        if (!value.trim()) return 'Value is required';
        break;
      case 'type_of_aquisition':
        if (!value.trim()) return 'Type of acquisition is required';
        break;
      case 'doc':
        if (!isEditMode && !value) return 'Document upload is required';
        break;
      default:
        break;
    }
    return '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const errorMsg = validateField(name, value);
    setFormFields(prev => ({
      ...prev,
      [name]: { value, error: !!errorMsg, helperText: errorMsg },
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      const errorMsg = validateField('doc', file);
      setFormFields(prev => ({
        ...prev,
        doc: { value: file, error: !!errorMsg, helperText: errorMsg },
      }));
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newFields = { ...formFields };
    Object.keys(newFields).forEach(name => {
      const errMsg = validateField(name, newFields[name].value);
      if (errMsg) isValid = false;
      newFields[name] = { ...newFields[name], error: !!errMsg, helperText: errMsg };
    });
    setFormFields(newFields);
    return isValid;
  };

  const handleFormSubmit = async () => {
    if (!validateForm()) return;

    const payload = new FormData();
    payload.append('financial_establishment', formFields.financial_establishment.value);
    payload.append('property_type', formFields.property_type.value);
    payload.append('location', formFields.location.value);
    payload.append('date_of_aquisition', formFields.date_of_aquisition.value);
    payload.append('value', formFields.value.value);
    payload.append('type_of_aquisition', formFields.type_of_aquisition.value);
    if (formFields.doc.value) {
      payload.append('doc', formFields.doc.value);
    }

    try {
      setUploading(true);
      setUploadProgress(0);
      if (isEditMode) {
        await api.patch(`/fe-mgmt/fe-personal-properties/${selectedItem.id}/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: event => setUploadProgress(Math.round((event.loaded * 100) / event.total)),
        });
        setSnackbarMessage('Personal Property updated successfully.');
      } else {
        await api.post(`/fe-mgmt/fe-personal-properties/`, payload, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: event => setUploadProgress(Math.round((event.loaded * 100) / event.total)),
        });
        setSnackbarMessage('Personal Property added successfully.');
      }
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchPersonalProperties();
      setOpenFormDialog(false);
    } catch {
      setSnackbarMessage('Failed to submit Personal Property.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (loading) return <LoadingSpinner size={30} />;

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 1, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', backgroundColor: '#fff' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="rgba(195,234,203,0.1)" p={2}>
        <Typography variant="h4" component="h1" fontWeight="bold">Personal Properties</Typography>
        <Button variant="contained" color="primary" onClick={handleAddItem} disabled={uploading}>Add</Button>
      </Box>
      <TableComponent columns={columns} rows={rows} manageColumnStatus enableSort sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#1976d2', color: '#fff', fontWeight: 'bold' } }} />
      <Dialog open={openFormDialog} onClose={() => !uploading && setOpenFormDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Personal Property' : 'Add Personal Property'}</DialogTitle>
        <DialogContent>
          <TextField select margin="dense" label="FE Base" fullWidth name="financial_establishment"
            value={formFields.financial_establishment.value} error={formFields.financial_establishment.error} helperText={formFields.financial_establishment.helperText}
            onChange={handleInputChange} disabled={uploading}>
            {feBaseList.map(fe => <MenuItem key={fe.id} value={fe.id}>{fe.name_of_fe || fe.id}</MenuItem>)}
          </TextField>
          <TextField margin="dense" label="Property Type" fullWidth name="property_type"
            value={formFields.property_type.value} error={formFields.property_type.error} helperText={formFields.property_type.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <TextField margin="dense" label="Location" fullWidth multiline minRows={2} name="location"
            value={formFields.location.value} error={formFields.location.error} helperText={formFields.location.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <TextField margin="dense" label="Date of Acquisition" type="date" fullWidth name="date_of_aquisition"
            value={formFields.date_of_aquisition.value} error={formFields.date_of_aquisition.error} helperText={formFields.date_of_aquisition.helperText}
            onChange={handleInputChange} disabled={uploading} InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" label="Value" fullWidth name="value"
            value={formFields.value.value} error={formFields.value.error} helperText={formFields.value.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <TextField margin="dense" label="Type of Acquisition" fullWidth name="type_of_aquisition"
            value={formFields.type_of_aquisition.value} error={formFields.type_of_aquisition.error} helperText={formFields.type_of_aquisition.helperText}
            onChange={handleInputChange} disabled={uploading} />
          <Button variant="outlined" component="label" sx={{ mt: 2 }}>
            {formFields.doc.value ? formFields.doc.value.name : 'Upload Document'}
            <input type="file" hidden name="doc" onChange={handleFileChange} disabled={uploading} />
          </Button>
          {formFields.doc.error && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{formFields.doc.helperText}</Typography>}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>Uploading... {uploadProgress}%</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !uploading && setOpenFormDialog(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleFormSubmit} variant="contained" color="primary" disabled={uploading}>{isEditMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this Personal Property?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" color="error" disabled={uploading}>Delete</Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponent open={snackbarOpen} message={snackbarMessage} severity={snackbarSeverity} onClose={handleSnackbarClose} />
    </Box>
  );
};
